<template>
    <div class="report-prep">
        <div class="report-prep__header">
            <h1 class="report-prep__title">Project Comps</h1>
        </div>

        <div class="report-prep__content">
            <!-- First Row: Project, Scenario, Comps -->
            <div class="grid grid-cols-3 gap-6 mb-8">
                <!-- Project Selection -->
                <themed-form-field label="Project" required>
                    <template #default="{ fieldId }">
                        <themed-select
                            :id="fieldId"
                            v-model="selectedProject"
                            :options="projectOptions"
                            placeholder="Select a project..."
                            @input="onProjectChange"
                        />
                    </template>
                </themed-form-field>

                <!-- Scenario Selection -->
                <themed-form-field label="Scenario" required>
                    <template #default="{ fieldId }">
                        <themed-select
                            :id="fieldId"
                            v-model="selectedScenario"
                            :options="scenarioOptions"
                            placeholder="Select a project first..."
                            :disabled="!selectedProject"
                        />
                    </template>
                </themed-form-field>

                <!-- Comps Selection -->
                <themed-form-field label="Comps" required>
                    <template #default="{ fieldId }">
                        <themed-multi-select
                            :id="fieldId"
                            v-model="selectedComps"
                            :options="filteredCompOptions"
                            placeholder="Select a project first..."
                            :disabled="!selectedProject"
                        />
                    </template>
                </themed-form-field>
            </div>

            <!-- Second Row: Options and Include -->
            <div class="grid grid-cols-2 gap-6 mb-8">
                <!-- Options Section -->
                <themed-form-field label="Options">
                    <div class="space-y-2">
                        <themed-checkbox
                            v-model="favoritesOnly"
                            label="Favorites Only"
                            @input="onFavoritesToggle"
                        />
                    </div>
                </themed-form-field>

                <!-- Include Section -->
                <themed-form-field label="Include">
                    <div class="space-y-2">
                        <themed-checkbox v-model="includeKsds" label="KSDs" />
                        <themed-checkbox v-model="includePhotos" label="Photos" />
                        <themed-checkbox v-model="includeUnitData" label="Unit Data" />
                        <themed-checkbox v-model="includePriceComparison" label="Comp Price Comparison" />
                        <themed-checkbox v-model="includeComments" label="Comments" />
                    </div>
                </themed-form-field>
            </div>

            <!-- Generate Button -->
            <div class="report-prep__actions">
                <themed-button
                    @click="generateReport"
                    variant="default"
                    size="lg"
                    :disabled="!canGenerate"
                >
                    Generate Report
                </themed-button>
            </div>
        </div>
    </div>
</template>

<script>
import ThemedButton from '../ThemedButton.vue';
import ThemedCheckbox from '../ThemedCheckbox.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedMultiSelect from '../ThemedMultiSelect.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import axios from 'axios';

export default {
    name: 'ProjectCompsPrep',
    
    components: {
        ThemedButton,
        ThemedCheckbox,
        ThemedSelect,
        ThemedMultiSelect,
        ThemedFormField
    },

    props: {
        projects: {
            type: Array,
            default: () => []
        },
        reportSlug: {
            type: String,
            default: 'project-comps'
        }
    },

    data() {
        return {
            selectedProject: null,
            selectedScenario: null,
            selectedComps: [],
            scenarios: [],
            comps: [],
            favoritesOnly: false,
            includeKsds: true,
            includePhotos: true,
            includeUnitData: true,
            includePriceComparison: true,
            includeComments: true
        };
    },

    computed: {
        projectOptions() {
            return this.projects.map(project => ({
                value: project.id,
                label: project.name
            }));
        },

        scenarioOptions() {
            return this.scenarios.map(scenario => ({
                value: scenario.id,
                label: scenario.name
            }));
        },

        compOptions() {
            return this.comps.map(comp => ({
                value: comp.id,
                label: comp.address_line_1,
                isFavorite: comp.pivot?.is_favorite || false
            }));
        },

        filteredCompOptions() {
            if (this.favoritesOnly) {
                return this.compOptions.filter(comp => comp.isFavorite);
            }
            return this.compOptions;
        },

        canGenerate() {
            return this.selectedProject && this.selectedScenario && this.selectedComps.length > 0;
        }
    },

    methods: {
        async onProjectChange() {
            this.selectedScenario = null;
            this.selectedComps = [];
            this.scenarios = [];
            this.comps = [];

            if (this.selectedProject) {
                await Promise.all([
                    this.loadScenarios(),
                    this.loadComps()
                ]);
            }
        },

        async loadScenarios() {
            try {
                const response = await axios.get(`/api/project/${this.selectedProject}/scenarios`);
                this.scenarios = response.data;
                
                // Auto-select first scenario if available
                if (this.scenarios.length > 0) {
                    this.selectedScenario = this.scenarios[0].id;
                }
            } catch (error) {
                console.error('Error loading scenarios:', error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to load scenarios'
                });
            }
        },

        async loadComps() {
            try {
                const response = await axios.get(`/api/project/${this.selectedProject}/comps`);
                this.comps = response.data;
            } catch (error) {
                console.error('Error loading comps:', error);
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Failed to load comps'
                });
            }
        },

        onFavoritesToggle() {
            // Clear selection when toggling favorites
            this.selectedComps = [];
        },

        generateReport() {
            if (!this.canGenerate) {
                this.$notify({
                    type: 'warning',
                    title: 'Missing Information',
                    text: 'Please select a project, scenario, and at least one comp.'
                });
                return;
            }

            const filters = {
                projectID: this.selectedProject,
                scenarioID: this.selectedScenario,
                compIDs: this.selectedComps,
                include_ksds: this.includeKsds,
                include_photos: this.includePhotos,
                include_unit_data: this.includeUnitData,
                include_price_comparison: this.includePriceComparison,
                include_comments: this.includeComments
            };

            const encodedFilters = btoa(JSON.stringify(filters));
            window.location = `/reports/generate/${this.reportSlug}?filters=${encodedFilters}`;
        }
    }
};
</script>
